import { isJobEvent, isJobNonExtEvent, isExtEvent } from '@libs/EventTypes';

export const isJobEventActive = (eventDate) => {
    return isJobNonExtEvent(eventDate.EventType) && eventDate.OptionNumber === 10 && !eventDate.Cancel;
};

export const isExtEventActive = (eventDate) => {
    return isExtEvent(eventDate.EventType) && eventDate.OptionNumber === 10 && !eventDate.Cancel;
};

export const isEventActive = (eventDate) => {
    return isJobEvent(eventDate.EventType) && eventDate.OptionNumber === 10 && !eventDate.Cancel;
};

const getProjectModelConfirmationPriority = (model) => {
    if (model.HasConfirmation) return 0;
    if (!model.AlwaysCancelled) return 1;
    return 2;
};

export const sortProjectModelsByConfirmation = (models, getModelName = (model) => model.ModelName || '') => {
    return [...models].sort((a, b) => {
        const priorityA = getProjectModelConfirmationPriority(a);
        const priorityB = getProjectModelConfirmationPriority(b);

        if (priorityA !== priorityB) {
            return priorityA - priorityB;
        }

        return (getModelName(a) || '').localeCompare(getModelName(b) || '');
    });
};

const getDefaultProjectModelEventDateDetails = ({ event, eventDateModel }) => ({
    EventID: event.ID,
    EventType: event.TypeName,
    OptionNumber: eventDateModel.OptionNumber,
    Cancel: eventDateModel.Cancel,
});

export const getProjectModelsWithEventDates = (
    project,
    getModelName,
    getEventDateDetails = getDefaultProjectModelEventDateDetails
) => {
    if (!project?.ProjectModels || !project?.Events) {
        return [];
    }

    const models = project.ProjectModels.map((model) => ({
        ModelID: model.ModelID,
        ModelName: getModelName(model.ModelID),
        Active: false,
        EventDates: [],
    }));

    for (const event of project.Events) {
        for (const eventDate of event.EventDates || []) {
            for (const eventDateModel of eventDate.EventDatesModels || []) {
                const modelIndex = models.findIndex((model) => model.ModelID === eventDateModel.ModelID);
                const eventDateDetails = getEventDateDetails({ event, eventDate, eventDateModel });

                if (modelIndex > -1) {
                    models[modelIndex].EventDates.push(eventDateDetails);
                } else {
                    models.push({
                        ModelID: eventDateModel.ModelID,
                        ModelName: getModelName(eventDateModel.ModelID),
                        Active: false,
                        EventDates: [eventDateDetails],
                    });
                }
            }
        }
    }

    return models;
};

const isSameDate = (date1string, date2string) => {
    if (date1string && date2string) {
        if (date1string.indexOf('T') > -1) {
            date1string = date1string.split('T')[0];
        }
        if (date2string.indexOf('T') > -1) {
            date2string = date2string.split('T')[0];
        }
        if (date1string === date2string) {
            return true;
        }
    }
    return false;
};

export const getTransactionConfirmationProperties = (project, transaction) => {
    const confprops = {
        Confirmed: null,
        OptionNumber: null,
        Cancel: null,
    };
    if (transaction.ModelID) {
        if (transaction.EventID) {
            const event = project.Events.find((event) => event.ID === transaction.EventID);
            if (event) {
                if (transaction.WorkDate) {
                    const eventDate = event.EventDates.find((ed) => isSameDate(ed.FromDate, transaction.WorkDate));
                    if (eventDate) {
                        const eventDateModel = eventDate.EventDatesModels.find(
                            (edm) => edm.ModelID === transaction.ModelID
                        );
                        if (eventDateModel) {
                            if (isJobEvent(event.TypeName)) {
                                confprops.OptionNumber = eventDateModel.OptionNumber;
                                confprops.Confirmed = eventDateModel.OptionNumber === 10 && !eventDateModel.Cancel;
                            } else {
                                confprops.OptionNumber = null;
                                confprops.Confirmed = !eventDateModel.Cancel;
                            }
                            confprops.Cancel = eventDateModel.Cancel;
                        } else {
                            confprops.Confirmed = false;
                            confprops.OptionNumber = null;
                            confprops.Cancel = null;
                        }
                    }
                } else {
                    const eventDateModel = event.EventDates.some((ed) =>
                        ed.EventDatesModels.find(
                            (edm) =>
                                edm.ModelID === transaction.ModelID &&
                                !edm.Cancel &&
                                (!isJobEvent(event.TypeName) || edm.OptionNumber === 10)
                        )
                    );
                    if (eventDateModel) {
                        confprops.Confirmed = true;
                        confprops.OptionNumber = null;
                        confprops.Cancel = false;
                    } else {
                        confprops.Confirmed = false;
                        confprops.OptionNumber = null;
                        confprops.Cancel = null;
                    }
                }
            }
        } else {
            if (transaction.TravelID) {
                const travel = project.Travels.find((travel) =>
                    travel.GroupedEventIDs.some((eventID) => eventID === transaction.TravelID)
                );
                const modelTravel = travel.Models.find((modelTravel) => modelTravel.ModelID === transaction.ModelID);
                if (modelTravel) {
                    confprops.Confirmed = true;
                    confprops.OptionNumber = 10;
                    confprops.Cancel = modelTravel.Cancel;
                }
            }
            if (transaction.AccommodationID) {
                const accommodation = project.Accommodations.find((accommodation) =>
                    accommodation.GroupedEventIDs.some((eventID) => eventID === transaction.AccommodationID)
                );
                const modelAccommodation = accommodation.Models.find(
                    (modelAccommodation) => modelAccommodation.ModelID === transaction.ModelID
                );
                if (modelAccommodation) {
                    confprops.Confirmed = true;
                    confprops.OptionNumber = 10;
                    confprops.Cancel = false;
                }
            }
            if (transaction.FollowUpID) {
                const followUp = project.FollowUps.find((followUp) =>
                    followUp.GroupedEventIDs.some((eventID) => eventID === transaction.FollowUpID)
                );
                const modelFollowUp = followUp.Models.find(
                    (modelFollowUp) => modelFollowUp.ModelID === transaction.ModelID
                );
                if (followUp) {
                    confprops.Confirmed = true;
                    confprops.OptionNumber = 10;
                    confprops.Cancel = modelFollowUp.Cancel;
                }
            }
        }
    }
    return confprops;
};

export const getModelConfirmationProperties = (project, modelID) => {
    const confprops = {
        ConfirmedWorkDays: 0,
    };
    if (modelID) {
        const events = project.Events.filter(
            (event) => isJobEvent(event.TypeName) && event.EventModels.some((em) => em.ModelID === modelID)
        );
        if (events.length > 0) {
            for (const event of events) {
                const confirmedDates = event.EventDates.filter((ed) =>
                    ed.EventDatesModels.find((edm) => edm.ModelID === modelID && !edm.Cancel && edm.OptionNumber === 10)
                );
                if (confirmedDates) {
                    confprops.ConfirmedWorkDays += confirmedDates.length;
                }
            }
        }
    }
    return confprops;
};
